import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, CalendarCheck, User, Phone as PhoneIcon } from "lucide-react";
import { topDwellTopic } from "@/lib/dwell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const greeting = "How may I be of service today?";
const words = greeting.split(" ");

const GENERAL_GREETINGS = [
  "Welcome to Crown Artisan Design.\nI'm here to help you explore ideas, answer questions about your project, or help you schedule a consultation. What are you dreaming of building?",
  "Good to have you here.\nWhether you're planning a luxury kitchen, a whole-home renovation, or a custom architectural feature, I'm here to help.",
  "Every exceptional home starts with a conversation.\nTell me about your project, and let's see what's possible.",
  "Planning something extraordinary?\nI'd love to hear about your vision and help you take the next step.",
];

const CONTEXT_GREETINGS = {
  kitchen:
    "Thinking about a luxury kitchen? Ask me anything—from design ideas to budgeting or timelines.",
  bathroom:
    "Looking to create a spa-quality bathroom? I can help you explore finishes, layouts, and what's possible.",
  wholehome:
    "Whole-home renovations are a big investment. I'm here to answer questions and help you plan with confidence.",
};

const BOOK_CHIP = "Book a consultation";
const suggestions = [
  BOOK_CHIP,
  "Design a wine cellar & tasting room",
  "Custom kitchen with Venetian plaster",
];

const BOOKING_INTENT = /\b(book|schedule|set ?up|arrange|appointment|consultation|come out|visit|meet)\b/i;

export default function Concierge() {
  const [sessionId] = useState(() => `sess_${Math.random().toString(36).slice(2)}${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatGreeting, setChatGreeting] = useState(
    () => GENERAL_GREETINGS[Math.floor(Math.random() * GENERAL_GREETINGS.length)]
  );
  // booking state
  const [booking, setBooking] = useState(false);
  const [bBusy, setBBusy] = useState(false);
  const [bForm, setBForm] = useState({ name: "", phone: "", date: "" });

  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const greetingSet = useRef(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !greetingSet.current) {
            greetingSet.current = true;
            const topic = topDwellTopic();
            if (topic && CONTEXT_GREETINGS[topic]) {
              setChatGreeting(CONTEXT_GREETINGS[topic]);
            }
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, booking]);

  const openBooking = () => {
    if (booking) return;
    setMessages((m) => [
      ...m,
      { role: "assistant", content: "I'd be delighted to arrange a private consultation. Share your name, phone, and preferred date below and I'll book it right away." },
    ]);
    setBooking(true);
  };

  const submitBooking = async () => {
    const { name, phone, date } = bForm;
    if (!name.trim() || !phone.trim() || !date || bBusy) return;
    setBBusy(true);
    try {
      const res = await fetch(`${API}/consultations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(), date }),
      });
      if (!res.ok) throw new Error("failed");
      const nice = new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
      setBooking(false);
      setBForm({ name: "", phone: "", date: "" });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Wonderful, ${name.trim()} — your consultation is booked for ${nice}. Our team will call you at ${phone.trim()} to confirm the details. We can't wait to bring your vision to life.` },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "My apologies — I couldn't complete the booking just now. Please try again, or call us at (817) 932-1473." },
      ]);
    } finally {
      setBBusy(false);
    }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;

    // explicit booking intent → open the in-chat booking card
    if (BOOKING_INTENT.test(content)) {
      setInput("");
      setMessages((m) => [...m, { role: "user", content }]);
      openBooking();
      return;
    }

    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const sanitize = (t) => t.replace(/bespoke/gi, "custom");
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: sanitize(copy[copy.length - 1].content + chunk),
          };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "My apologies — I'm momentarily unavailable. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onSuggestion = (s) => {
    if (s === BOOK_CHIP) {
      setMessages((m) => [...m, { role: "user", content: "I'd like to book a consultation." }]);
      openBooking();
    } else {
      send(s);
    }
  };

  return (
    <section ref={sectionRef} id="concierge" className="relative py-24 lg:py-36 border-t border-white/5" data-testid="concierge-section">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-6">
            <Sparkles size={14} /> AI Concierge
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl text-white flex flex-wrap justify-center gap-x-4 gap-y-1">
            {words.map((w, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 40, scale: 0.6 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={i >= words.length - 2 ? "gold-fill" : ""}
              >
                {w}
              </motion.span>
            ))}
          </h2>
        </div>

        <div className="glass rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: "0 0 80px rgba(212,175,55,0.08)" }}>
          <div
            ref={scrollRef}
            className="h-[360px] overflow-y-auto p-6 space-y-4"
            data-testid="chat-messages"
          >
            {messages.length === 0 && !booking && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-6">
                <p className="text-white/70 font-cormorant text-xl sm:text-2xl max-w-md whitespace-pre-line leading-relaxed" data-testid="chat-greeting">
                  {chatGreeting}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => onSuggestion(s)}
                      data-testid={s === BOOK_CHIP ? "chat-book-suggestion" : "chat-suggestion"}
                      className={`text-xs px-4 py-2 rounded-full border transition-colors ${
                        s === BOOK_CHIP
                          ? "border-[var(--gold)]/70 text-[var(--gold)] hover:bg-[var(--gold)]/10"
                          : "border-[var(--gold)]/30 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/70"
                      }`}
                    >
                      {s === BOOK_CHIP && <CalendarCheck size={12} className="inline mr-1.5 -mt-0.5" />}
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gradient-to-br from-[#FFD700] to-[#996515] text-black rounded-br-sm"
                      : "bg-white/5 border border-white/10 text-white/90 rounded-bl-sm"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1 items-center text-[var(--gold)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  )}
                </div>
              </div>
            ))}

            {booking && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-[var(--gold)]/30 bg-black/40 rounded-2xl p-5 max-w-[92%]"
                data-testid="booking-card"
              >
                <div className="flex items-center gap-2 text-[var(--gold)] text-xs tracking-[0.2em] uppercase mb-4">
                  <CalendarCheck size={14} /> Book a Consultation
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3">
                    <User size={15} className="text-white/40" />
                    <input
                      value={bForm.name}
                      onChange={(e) => setBForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      data-testid="booking-name"
                      className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 py-2.5 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3">
                    <PhoneIcon size={15} className="text-white/40" />
                    <input
                      value={bForm.phone}
                      onChange={(e) => setBForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone number"
                      type="tel"
                      data-testid="booking-phone"
                      className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 py-2.5 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3">
                    <CalendarCheck size={15} className="text-white/40" />
                    <input
                      value={bForm.date}
                      onChange={(e) => setBForm((f) => ({ ...f, date: e.target.value }))}
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      data-testid="booking-date"
                      className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 py-2.5 text-sm [color-scheme:dark]"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={submitBooking}
                    disabled={bBusy || !bForm.name.trim() || !bForm.phone.trim() || !bForm.date}
                    data-testid="booking-submit"
                    className="btn-gold rounded-full px-6 py-2.5 text-xs tracking-[0.16em] uppercase font-medium disabled:opacity-40"
                  >
                    {bBusy ? "Booking..." : "Confirm Booking"}
                  </button>
                  <button
                    onClick={() => setBooking(false)}
                    data-testid="booking-cancel"
                    className="text-xs tracking-[0.14em] uppercase text-white/50 hover:text-white/80 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-3 flex items-center gap-3 bg-black/30"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe your vision, or type 'book a consultation'..."
              data-testid="chat-input"
              className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="chat-send-btn"
              className="btn-gold rounded-full p-3 disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
